import Cookies from 'js-cookie'

// Domínio base para compartilhar cookies entre subdomínios
// Em desenvolvimento: .ecx.local
// Em produção: .ecxpay.com.br
const getCookieDomain = (): string | undefined => {
  const hostname = window.location.hostname
  
  if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
    return undefined
  }
  
  if (hostname.endsWith('.ecx.local')) {
    return '.ecx.local'
  }
  
  if (hostname.endsWith('.ecxpay.com.br')) {
    return '.ecxpay.com.br'
  }
  
  const parts = hostname.split('.')
  if (parts.length >= 2) {
    return '.' + parts.slice(-2).join('.')
  }
  
  return undefined
}

const COOKIE_OPTIONS = {
  domain: getCookieDomain(),
  secure: window.location.protocol === 'https:',
  sameSite: 'lax' as const,
  expires: 7, // 7 dias
}

// ========================================
// Tipos para Auth Multibenefícios (Pay)
// ========================================
export interface AuthCookieData {
  email: string
  token: string
  temporaryToken?: string
  validationTokenSentAt?: string
}

// ========================================
// Tipos para Auth Corp
// ========================================
export interface CorpCookieData {
  accessToken: string
  refreshToken: string
  accountId: string | null
  businessCnpj: string | null
  expiresAt: string // ISO date string
}

export interface CorpChallengeCookieData {
  challengeToken: string
  expiresIn: number
  otpSentTo: string
  createdAt: string // ISO date string
}

const AUTH_COOKIE_NAME = 'ecx_auth'
const TEMP_AUTH_COOKIE_NAME = 'ecx_temp_auth'

export const authCookies = {
  /**
   * Salva a sessão completa (após 2FA validado)
   */
  setSession(email: string, token: string): void {
    const data: AuthCookieData = { email, token }
    Cookies.set(AUTH_COOKIE_NAME, JSON.stringify(data), COOKIE_OPTIONS)
    // Remove cookie temporário
    this.clearTemporarySession()
  },

  /**
   * Salva a sessão temporária (após login, antes do 2FA)
   */
  setTemporarySession(email: string, temporaryToken: string, validationTokenSentAt: Date): void {
    const data: AuthCookieData = {
      email,
      temporaryToken,
      token: '',
      validationTokenSentAt: validationTokenSentAt.toISOString(),
    }
    Cookies.set(TEMP_AUTH_COOKIE_NAME, JSON.stringify(data), {
      ...COOKIE_OPTIONS,
      expires: 1, // 1 dia para token temporário
    })
  },

  /**
   * Retorna a sessão atual (se existir)
   */
  getSession(): AuthCookieData | null {
    const cookie = Cookies.get(AUTH_COOKIE_NAME)
    if (!cookie) return null
    
    try {
      return JSON.parse(cookie)
    } catch {
      return null
    }
  },

  /**
   * Retorna a sessão temporária (se existir)
   */
  getTemporarySession(): AuthCookieData | null {
    const cookie = Cookies.get(TEMP_AUTH_COOKIE_NAME)
    if (!cookie) return null
    
    try {
      return JSON.parse(cookie)
    } catch {
      return null
    }
  },

  /**
   * Verifica se há uma sessão válida
   */
  hasSession(): boolean {
    const session = this.getSession()
    return !!(session?.email && session?.token)
  },

  /**
   * Verifica se há uma sessão temporária (aguardando 2FA)
   */
  hasTemporarySession(): boolean {
    const session = this.getTemporarySession()
    return !!(session?.email && session?.temporaryToken)
  },

  /**
   * Limpa a sessão completa
   */
  clearSession(): void {
    Cookies.remove(AUTH_COOKIE_NAME, { domain: getCookieDomain() })
  },

  /**
   * Limpa a sessão temporária
   */
  clearTemporarySession(): void {
    Cookies.remove(TEMP_AUTH_COOKIE_NAME, { domain: getCookieDomain() })
  },

  /**
   * Limpa todas as sessões (logout)
   */
  clearAll(): void {
    this.clearSession()
    this.clearTemporarySession()
  },

  /**
   * Atualiza o timestamp de envio do token de validação
   */
  updateValidationTokenSentAt(sentAt: Date): void {
    const session = this.getTemporarySession()
    if (session) {
      session.validationTokenSentAt = sentAt.toISOString()
      Cookies.set(TEMP_AUTH_COOKIE_NAME, JSON.stringify(session), {
        ...COOKIE_OPTIONS,
        expires: 1,
      })
    }
  },
}

// ========================================
// Cookies para ECX Corp
// ========================================
const CORP_AUTH_COOKIE_NAME = 'ecx_corp_auth'
const CORP_CHALLENGE_COOKIE_NAME = 'ecx_corp_challenge'

export const corpCookies = {
  /**
   * Salva a sessão completa do Corp (após OTP validado)
   */
  setSession(
    accessToken: string,
    refreshToken: string,
    accountId: string | null,
    expiresIn: number,
    businessCnpj?: string | null
  ): void {
    const expiresAt = new Date(Date.now() + expiresIn * 1000)
    const data: CorpCookieData = {
      accessToken,
      refreshToken,
      accountId,
      businessCnpj: businessCnpj ?? null,
      expiresAt: expiresAt.toISOString(),
    }
    Cookies.set(CORP_AUTH_COOKIE_NAME, JSON.stringify(data), COOKIE_OPTIONS)
    this.clearChallenge()
  },

  /**
   * Retorna a sessão Corp atual (se existir)
   */
  getSession(): CorpCookieData | null {
    const cookie = Cookies.get(CORP_AUTH_COOKIE_NAME)
    if (!cookie) return null
    
    try {
      return JSON.parse(cookie)
    } catch {
      return null
    }
  },

  /**
   * Verifica se há uma sessão Corp válida
   */
  hasSession(): boolean {
    const session = this.getSession()
    if (!session?.accessToken) return false
    
    // Verifica se não expirou
    const expiresAt = new Date(session.expiresAt)
    return expiresAt > new Date()
  },

  /**
   * Limpa a sessão Corp
   */
  clearSession(): void {
    Cookies.remove(CORP_AUTH_COOKIE_NAME, { domain: getCookieDomain() })
  },

  /**
   * Salva o challenge token (após login, antes do OTP)
   */
  setChallenge(challengeToken: string, expiresIn: number, otpSentTo: string): void {
    const data: CorpChallengeCookieData = {
      challengeToken,
      expiresIn,
      otpSentTo,
      createdAt: new Date().toISOString(),
    }
    Cookies.set(CORP_CHALLENGE_COOKIE_NAME, JSON.stringify(data), {
      ...COOKIE_OPTIONS,
      expires: 1, // 1 dia (o token expira em 5 min, mas cookie dura mais)
    })
  },

  /**
   * Retorna o challenge atual (se existir)
   */
  getChallenge(): CorpChallengeCookieData | null {
    const cookie = Cookies.get(CORP_CHALLENGE_COOKIE_NAME)
    if (!cookie) return null
    
    try {
      return JSON.parse(cookie)
    } catch {
      return null
    }
  },

  /**
   * Verifica se há um challenge válido (não expirado)
   */
  hasValidChallenge(): boolean {
    const challenge = this.getChallenge()
    if (!challenge?.challengeToken) return false
    
    // Verifica se não expirou (5 minutos = 300 segundos)
    const createdAt = new Date(challenge.createdAt)
    const expiresAt = new Date(createdAt.getTime() + challenge.expiresIn * 1000)
    return expiresAt > new Date()
  },

  /**
   * Limpa o challenge
   */
  clearChallenge(): void {
    Cookies.remove(CORP_CHALLENGE_COOKIE_NAME, { domain: getCookieDomain() })
  },

  /**
   * Atualiza os tokens (usado no refresh)
   */
  updateTokens(accessToken: string, refreshToken: string, expiresIn: number): void {
    const session = this.getSession()
    if (session) {
      const expiresAt = new Date(Date.now() + expiresIn * 1000)
      session.accessToken = accessToken
      session.refreshToken = refreshToken
      session.expiresAt = expiresAt.toISOString()
      Cookies.set(CORP_AUTH_COOKIE_NAME, JSON.stringify(session), COOKIE_OPTIONS)
    }
  },

  /**
   * Limpa tudo (logout)
   */
  clearAll(): void {
    this.clearSession()
    this.clearChallenge()
  },

  /**
   * Obtém credenciais para headers
   */
  getCredentials(): { accessToken: string; accountId: string | null } | null {
    const session = this.getSession()
    if (session?.accessToken) {
      return {
        accessToken: session.accessToken,
        accountId: session.accountId,
      }
    }
    return null
  },
}

